<template>
  <div style="height: 100%" class="pa-4">
    <v-row justify="space-between" class="mb-4">
      <v-col v-if="!noHeader">
        <div class="font-weight-bold basic--text text-center">
          {{ "SSCE Details".toUpperCase() }}
        </div>
      </v-col>
    </v-row>
    <v-form ref="form" v-model="dataValid">
      <v-alert type="warning" class="mt-4">
        Make sure to provide correct information as editing will not be allowed
        after submission. Only five subjects are allowed combined from all
        sittings
      </v-alert>
      <div class="d-flex">
        <div class="basic--text font-weight-bold">Number of sittings</div>
        <div class="ml-4">
          <v-select
            :items="numberOfSittingsOptions"
            dense
            outlined
            :disabled="!canEdit"
            v-model="numberOfSitting"
            label="Number of sittings"
          ></v-select>
        </div>
      </div>
      <div v-if="numberOfSitting != null">
        <div v-for="ctr in numberOfSitting" class="mb-4" :key="ctr">
          <div class="d-flex">
            <div class="basic--text mb-2 font-weight-bold">
              Sitting {{ ctr }}
            </div>
            <v-spacer></v-spacer>
            <div class="basic--text font-weight-bold mr-4">
              Number of subjects for this sitting
            </div>
            <div>
              <v-text-field
                outlined
                dense
                :disabled="!canEdit"
                v-model="results[ctr - 1].numberOfSubjects"
                required
                :rules="nonZeroRules"
              ></v-text-field>
            </div>
          </div>
          <v-row justify="space-between">
            <v-col cols="12" md="6">
              <v-select
                :items="organizations"
                item-text="title"
                item-value="id"
                outlined
                dense
                label="Organization name"
                :rules="textRules"
                required
                :disabled="!canEdit"
                v-model="results[ctr - 1].organization"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                outlined
                dense
                :items="examTypes"
                label="Examination type"
                item-text="title"
                item-value="id"
                :rules="textRules"
                required
                :disabled="!canEdit"
                v-model="results[ctr - 1].examType"
              ></v-select>
            </v-col>
          </v-row>
          <v-row justify="space-between">
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                dense
                label="Registration number"
                :rules="textRules"
                required
                :disabled="!canEdit"
                v-model="results[ctr - 1].registrationNumber"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                outlined
                dense
                label="Year"
                :items="years"
                :rules="textRules"
                required
                :disabled="!canEdit"
                v-model="results[ctr - 1].year"
              ></v-select>
            </v-col>
          </v-row>
          <!--    Subject 1      -->
          <v-row v-if="(results[ctr - 1].numberOfSubjects > 0 && userData.length === 0) || results[ctr - 1].subject1">
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Subject 1"
                outlined
                :items="subjects"
                item-text="subject.name"
                item-value="subject.id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].subject1"
                @select="subjectChange(ctr - 1, 1)"
                @change="subjectChange(ctr - 1, 1)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Grade 1"
                outlined
                :items="results[ctr - 1].subject1 != null ? grades[results[ctr - 1].subject1] : []"
                item-text="title"
                item-value="id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].grade1"
                :ref="'grade1' + ctr"
              ></v-select>
            </v-col>
          </v-row>

          <!--    Subject 2      -->
          <v-row v-if="(results[ctr - 1].numberOfSubjects > 1 && userData.length === 0) || results[ctr - 1].subject2">
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Subject 2"
                outlined
                :items="subjects"
                item-text="subject.name"
                item-value="subject.id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].subject2"
                @select="subjectChange(ctr - 1, 2)"
                @change="subjectChange(ctr - 1, 2)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Grade 2"
                outlined
                :items="results[ctr - 1].subject2 != null ? grades[results[ctr - 1].subject2] : []"
                item-text="title"
                item-value="id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].grade2"
                :ref="'grade2' + ctr"
              ></v-select>
            </v-col>
          </v-row>

          <!--    Subject 3      -->
          <v-row v-if="(results[ctr - 1].numberOfSubjects > 2 && userData.length === 0) || results[ctr - 1].subject3">
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Subject 3"
                outlined
                :items="subjects"
                item-text="subject.name"
                item-value="subject.id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].subject3"
                @select="subjectChange(ctr - 1, 3)"
                @change="subjectChange(ctr - 1, 3)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                dense
                label="Grade 3"
                outlined
                :items="results[ctr - 1].subject3 != null ? grades[results[ctr - 1].subject3] : []"
                item-text="title"
                item-value="id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].grade3"
                :ref="'grade3' + ctr"
              ></v-select>
            </v-col>
          </v-row>

          <!--    Subject 4      -->
          <v-row class="my-0" v-if="(results[ctr - 1].numberOfSubjects > 3 && userData.length === 0) || results[ctr - 1].subject4">
            <v-col cols="12" md="6" class="my-0">
              <v-select
                dense
                label="Subject 4"
                outlined
                :items="subjects"
                item-text="subject.name"
                item-value="subject.id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].subject4"
                @select="subjectChange(ctr - 1, 4)"
                @change="subjectChange(ctr - 1, 4)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="my-0">
              <v-select
                dense
                label="Grade 4"
                outlined
                :items="results[ctr - 1].subject4 != null ? grades[results[ctr - 1].subject4] : []"
                item-text="title"
                item-value="id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].grade4"
                :ref="'grade4' + ctr"
              ></v-select>
            </v-col>
          </v-row>

          <!--    Subject 5      -->
          <v-row class="my-0" v-if="(results[ctr - 1].numberOfSubjects > 4 && userData.length === 0) || results[ctr - 1].subject5">
            <v-col cols="12" md="6" class="my-0">
              <v-select
                dense
                label="Subject 5"
                outlined
                :items="subjects"
                item-text="subject.name"
                item-value="subject.id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].subject5"
                @select="subjectChange(ctr - 1, 5)"
                @change="subjectChange(ctr - 1, 5)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="my-0">
              <v-select
                dense
                label="Grade 5"
                outlined
                :items="results[ctr - 1].subject5 != null ? grades[results[ctr - 1].subject5] : []"
                item-text="title"
                item-value="id"
                required
                :rules="textRules"
                :disabled="!canEdit"
                v-model="results[ctr - 1].grade5"
                :ref="'grade5' + ctr"
              ></v-select>
            </v-col>
          </v-row>
          <v-divider></v-divider>
        </div>
      </div>
    </v-form>
    <v-card-actions class="mt-6">
      <v-spacer></v-spacer>
      <v-btn color="basic" class="white--text" @click="$emit('back')" depressed>{{  noHeader ? 'Back' : 'Previous' }}</v-btn>
      <v-btn
        color="error"
        depressed
        @click="$refs.form.reset()"
        v-if="userData.length === 0 && !noHeader"
        >Reset</v-btn
      >
      <v-btn
        color="primary"
        depressed
        @click="saveData"
        :disabled="!dataValid || numberOfSitting === 0"
        v-if="userData.length === 0"
        >{{  noHeader ? 'Save' : 'Proceed' }}</v-btn
      >
      <v-btn
        color="info"
        depressed
        @click="success"
        v-if="!canEdit"
        >Skip</v-btn
      >
    </v-card-actions>
    <loading-dialog :show="loading"></loading-dialog>
    <message-box
      :show="messageShow"
      :message="message"
      :type="messageType"
      v-on:close-message="messageShow = !messageShow"
    ></message-box>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ValidationRules from "@/shared/ValidationRules";
import DateUtil from "@/shared/DateUtil";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";

export default {
  name: "SSCEDetails",
  props: {
    noHeader: Boolean
  },
  components: { MessageBox, LoadingDialog },
  data() {
    return {
      obj: {},
      subjects: [],
      textRules: ValidationRules.textRules,
      numberRules: ValidationRules.numericRules,
      nonZeroRules: ValidationRules.nonZeroNumbers,
      userData: [],
      dataValid: true,
      messageType: "",
      message: "",
      messageShow: false,
      loading: false,
      years: DateUtil.getYearsArray(1970),
      organizations: [],
      examTypes: [],
      grades: [],
      numberOfSitting: 0,
      results: [],
      numberOfSittingsOptions: [],
      notEqualToFive: true, // true at first
      edit: false
    };
  },
  methods: {
    success() {
      this.$emit("success");
    },

    saveData() {
      let fd = new FormData();
      // copy required
      let d = [];
      for (let i = 0; i < this.numberOfSitting; i++) d.push(this.results[i]);

      const json = JSON.stringify(d);
      const blob = new Blob([json], {
        type: "application/json",
      });

      fd.append("results", blob);
      this.loading = true;
      Api.applicants
        .ssceUpdate(this.getUser.id, fd)
        .then((res) => {
          this.message = "SSCE Details updated successfully";
          this.messageType = "success";
          this.messageShow = true;
          this.$store.commit("setSSCEDetails", res.data);
          this.success();
        })
        .catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => (this.loading = false));
    },

    loadBasicData() {
      this.loading = true;
      Api.requirements
        .getSsceRequirements()
        .then((res) => {
          this.examTypes = res.data.examTypes;
          this.organizations = res.data.organizations;
        })
        .catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => (this.loading = false));
    },

    loadSubjects() {
      if(this.getUser != null && this.getUser.programme != null){
        this.loading = true;
        Api.department
            .getSubjectsForApplications(this.getUser.programme.department.id)
            .then((res) => {
              this.subjects = res.data
              // populate grades
              for (let c = 0; c < res.data.length; c++){
                this.grades[res.data[c].subject.id] = res.data[c].grades
              }
            })
            .catch((err) => ErrorResolver.checkErrors(this, err))
            .finally(() => (this.loading = false));
      }
    },

    loadSSCEdDetails() {
      this.loading = true;
      Api.applicants
        .getSSCEDetails(this.getUser.userId)
        .then((res) => {
          this.userData = res.data;
          // update store
          this.$store.commit("setSSCEDetails", res.data);
          this.prefillData();
        })
        .catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => (this.loading = false));
    },

    prefillData() {
      this.numberOfSitting = this.userData.length;
      if (this.userData.length > 0) {
        this.numberOfSitting = this.userData.length;
        this.edit= true
        for (let i = 0; i < this.userData.length; i++) {
          let u = this.userData[i];
          console.log(u)
          this.results[i].scratchCardSerialNumber = u.scratchCardSerialNumber;
          this.results[i].scratchCardPin = u.scratchCardPin;
          this.results[i].organization = u.organization.id;
          this.results[i].examType = u.examType.id;
          this.results[i].registrationNumber = u.registrationNumber;
          this.results[i].year = +u.year;
          if (u.subject1 !== null) {
            this.results[i].subject1 = u.subject1.id;
            this.results[i].grade1 = u.grade1.id;
            this.results[i].numberOfSubjects++;
          }
          if (u.subject2 !== null) {
            this.results[i].subject2 = u.subject2.id;
            this.results[i].grade2 = u.grade2.id;
            this.results[i].numberOfSubjects++;
          }
          if (u.subject3 !== null) {
            this.results[i].subject3 = u.subject3.id;
            this.results[i].grade3 = u.grade3.id;
            this.results[i].numberOfSubjects++;
          }
          if (u.subject4 !== null) {
            this.results[i].subject4 = u.subject4.id;
            this.results[i].grade4 = u.grade4.id;
            this.results[i].numberOfSubjects++;
          }
          if (u.subject5 !== null) {
            this.results[i].subject5 = u.subject5.id;
            this.results[i].grade5 = u.grade5.id;
            this.results[i].numberOfSubjects++;
          }
        }

        this.edit = false
      }
    },

    check() {
      this.loading = true;
      Api.applicants
        .nextToSSCEUpdate(this.getUser.id)
        .then((res) => {
          this.loading = false;
          this.$store.commit("setCurrentUser", res.data);
        })
        .catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => (this.loading = false));
    },

    fillResultsWithDefaults() {
      if (this.results.length > 0) {
        for (let i = 0; i < 10; i++) {
          this.results[i] = {
            numberOfSubjects: 0,
          };
        }
      } else {
        for (let i = 0; i < 10; i++) {
          this.results.push({
            numberOfSubjects: 0,
          });
        }
      }
    },

    subjectChange(index, subjectNo){
      if(subjectNo === 1){
        this.$refs['grade1' + (index + 1)][0].reset()
      } else if(subjectNo === 2){
        this.$refs['grade2' + (index + 1)][0].reset()
      } else if(subjectNo === 3){
        this.$refs['grade3' + (index + 1)][0].reset()
      } else if(subjectNo === 4){
        this.$refs['grade4' + (index + 1)][0].reset()
      } else if(subjectNo === 5){
        this.$refs['grade5' + (index + 1)][0].reset()
      }
    },

    noOfSubjectChange(index){
      this.results[index].grade1 = null
      this.results[index].grade2 = null
      this.results[index].grade3 = null
      this.results[index].grade4 = null
      this.results[index].grade5 = null
    }
  },

  computed: {
    ...mapGetters(["getUser", "getInformation"]),
    canEdit: function(){
      return this.userData.length < 1
    }
  },

  watch: {
    getUser: function (val) {
      if (this.getUser.admission.applicationType.ssceDetailsEnabled === false) {
        this.success();
        return;
      }
      if (this.numberOfSittingsOptions.length < 1) {
        if (val != null) {
          for (
            let i = 1;
            i <= this.getUser.admission.applicationType.numberOfSittings;
            i++
          )
            this.numberOfSittingsOptions.push(i);
        }
      }
    },

    userData: function (val) {
      if (val != null && val.length > 0) {
        // this.success()
      }
    },
  },

  created() {
    if (this.getUser != null) {
      if (this.getUser.admission.applicationType.ssceDetailsEnabled === false) {
        this.success();
        return;
      }

      this.check();
      for (
        let i = 1;
        i <= this.getUser.admission.applicationType.numberOfSittings;
        i++
      )
        this.numberOfSittingsOptions.push(i);
    }

    this.loadBasicData();
    this.loadSubjects();
    this.fillResultsWithDefaults();
    this.loadSSCEdDetails();
    this.noHeader && this.$emit('update-title', "SSCE Details")
  },
};
</script>

<style scoped>
</style>
